"use client"
import cplTrainingImg from "../../../assets/Program/CPLSA.JPG";
import airlineReadyImg from "../../../assets/Program/CPLHU.JPG";
import specializedTrainingImg from "../../../assets/Program/CPLOTHER.JPG";
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom";
import { Plane, Calendar, CheckCircle, ShieldCheck, Euro, PlaneTakeoff, GraduationCap, Award, BookOpen, Users, Compass, RefreshCw, Globe } from 'lucide-react'; // Added import for lucide-react icons

const ProgramOverview = () => {
  const [hoveredCard, setHoveredCard] = useState(null)
  const navigate = useNavigate();

  const handleCardClick = (link) => {
    if (link) {
      navigate(link);
    }
  }

  const programData = {
    "CPL South Africa": {
      image: cplTrainingImg,
      link: "/commercial-pilot-license",
      badge: "12-14 Months",
      shortDesc: "Complete your Commercial Pilot License in sunny South Africa with 300+ flying days annually.",
      details: [
        "Duration: 12-14 months with flexible scheduling",
        "Weather: 300+ flying days per year - ideal conditions",
        "Training: Multi-engine and Instrument Rating included",
        "Cost: Most affordable CPL program globally",
        "Locations: Multiple training bases across South Africa",
        "Aircraft: Modern fleet with state-of-the-art equipment"
      ],
      highlights: [
        { icon: <Calendar size={18} />, text: "300+ Flying Days" },
        { icon: <PlaneTakeoff size={18} />, text: "Multi-Engine" },
        { icon: <Euro size={18} />, text: "Affordable" },
        { icon: <CheckCircle size={18} />, text: "EASA Approved" }
      ]
    },
    "CPL Hungary": {
      image: airlineReadyImg,
      link: "/zero-to-hero",
      badge: "16-18 Months",
      shortDesc: "Become airline-ready with our comprehensive Zero to Hero training program in Europe.",
      details: [
        "Duration: 16-18 months intensive training",
        "Certification: Complete Zero to Hero airline preparation",
        "Modules: Multi-engine, IR, MCC, and UPRT included",
        "Standards: European aviation training standards",
        "Cooperation: Multi-Crew Cooperation (MCC) training",
        "Safety: Upset Prevention & Recovery Training (UPRT)"
      ],
      highlights: [
        { icon: <Globe size={18} />, text: "European Training" },
        { icon: <Users size={18} />, text: "MCC Certified" },
        { icon: <ShieldCheck size={18} />, text: "UPRT Included" },
        { icon: <Plane size={18} />, text: "Airline Ready" }
      ]
    },
    "Other Training": {
      image: specializedTrainingImg,
      link: "/courses",
      badge: "Various Durations",
      shortDesc: "Advance your aviation career with specialized training programs and type ratings.",
      details: [
        "Cabin Crew: Complete cabin crew training programs",
        "FI Rating: Flight Instructor certification courses",
        "A320 Type: Airbus A320 family type rating",
        "B737 Type: Boeing 737 family type rating",
        "Custom: Tailored training solutions available",
        "Recurrent: Refresher and recurrent training"
      ],
      highlights: [
        { icon: <Award size={18} />, text: "Type Ratings" },
        { icon: <GraduationCap size={18} />, text: "FI Courses" },
        { icon: <BookOpen size={18} />, text: "Cabin Crew" },
        { icon: <RefreshCw size={18} />, text: "Recurrent" }
      ]
    }
  }

  const styles = {
    container: {
      minHeight: "100vh",
      background: "#ffffff",
      padding: "clamp(60px, 10vh, 100px) clamp(20px, 5vw, 80px)", /* MATCHED PADDING WITH OTHER COMPONENTS */
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      boxSizing: "border-box",
    },
    header: {
      textAlign: "left", /* ALIGN LEFT */
      marginBottom: "clamp(40px, 8vw, 80px)",
      maxWidth: "1400px", /* MATCHED CONTAINER WIDTH */
      margin: "0 auto clamp(40px, 8vw, 80px)",
      width: "100%",
    },
    title: {
      fontSize: "clamp(28px, 5vw, 44px)", /* MATCHED HEADLINE SIZE WITH OTHER COMPONENTS */
      fontWeight: "700",
      color: "#0284c7",
      marginBottom: "16px",
      lineHeight: 1.2,
    },
    subtitle: {
      fontSize: "clamp(16px, 3vw, 20px)",
      color: "#6c757d",
      lineHeight: 1.6,
      maxWidth: "700px",
      margin: "0", /* RESET MARGIN TO KEEP LEFT ALIGNMENT */
    },
    cardsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 350px), 1fr))",
      gap: "clamp(24px, 4vw, 40px)",
      maxWidth: "1400px",
      margin: "0 auto",
    },
    card: {
      background: "white",
      borderRadius: "20px",
      overflow: "hidden",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      position: "relative",
    },
    cardHovered: {
      transform: "translateY(-8px)",
      boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
    },
    imageContainer: {
      position: "relative",
      width: "100%",
      height: "280px",
      overflow: "hidden",
    },
    cardImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "transform 0.4s ease",
    },
    imageOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.4) 100%)",
      pointerEvents: "none",
    },
    badge: {
      position: "absolute",
      top: "20px",
      right: "20px",
      background: "rgba(0, 0, 0, 0.85)",
      backdropFilter: "blur(10px)",
      color: "white",
      padding: "8px 16px",
      borderRadius: "30px",
      fontSize: "13px",
      fontWeight: "600",
      letterSpacing: "0.5px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
    },
    cardContent: {
      padding: "clamp(24px, 4vw, 32px)",
    },
    cardTitle: {
      fontSize: "clamp(22px, 4vw, 28px)",
      fontWeight: "700",
      color: "#212529",
      margin: "0 0 16px 0",
      lineHeight: 1.3,
    },
    cardDescription: {
      fontSize: "clamp(14px, 2.5vw, 16px)",
      color: "#6c757d",
      lineHeight: 1.7,
      marginBottom: "20px",
    },
    highlightsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "12px",
      marginBottom: "20px",
    },
    highlightItem: {
      display: "flex",
      alignItems: "center",
      padding: "10px",
      background: "#f8f9fa",
      borderRadius: "10px",
      fontSize: "13px",
      fontWeight: "500",
      color: "#495057",
    },
    viewDetailsBtn: {
      width: "100%",
      padding: "14px",
      border: "2px solid transparent",
      borderRadius: "12px",
      fontSize: "15px",
      fontWeight: "600",
      color: "white",
      background: "#000000",
      cursor: "pointer",
      transition: "all 0.3s ease",
      pointerEvents: "none", /* To prevent conflicting with the card's click event */
    },
  }

  return (
    <section style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Our Training Programs</h1>
        <p style={styles.subtitle}>
          Choose from our world-class aviation training programs designed to launch and advance your flying career
        </p>
      </div>

      <div style={styles.cardsGrid}>
        {Object.entries(programData).map(([programKey, program]) => (
          <div
            key={programKey}
            style={{
              ...styles.card,
              ...(hoveredCard === programKey ? styles.cardHovered : {}),
            }}
            onMouseEnter={() => setHoveredCard(programKey)}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => handleCardClick(program.link)}
          >
            <div style={styles.imageContainer}>
              <img
                src={program.image}
                alt={programKey}
                style={{
                  ...styles.cardImage,
                  transform: hoveredCard === programKey ? "scale(1.05)" : "scale(1)",
                }}
              />
              <div style={styles.imageOverlay} />
              <div style={styles.badge}>{program.badge}</div>
            </div>

            <div style={styles.cardContent}>
              <h3 style={styles.cardTitle}>{programKey}</h3>
              <p style={styles.cardDescription}>{program.shortDesc}</p>

              <div style={styles.highlightsGrid}>
                {program.highlights.map((highlight, index) => (
                  <div key={index} style={styles.highlightItem}>
                    <span style={{ display: 'flex', alignItems: 'center', marginRight: '8px', color: '#0284c7' }}>
                      {highlight.icon}
                    </span>
                    <span>{highlight.text}</span>
                  </div>
                ))}
              </div>

              <button
                style={{
                  ...styles.viewDetailsBtn,
                  background: hoveredCard === programKey ? "#0284c7" : "#000000"
                }}
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default ProgramOverview
